import {Box, HBox} from '../../../components/core/view';
import {
  DeliveryLocationStatus,
  GroupedAssignedItems,
  OrderItemStatus,
  OrderItems,
  RunForBatchDetail,
  itemsForSection,
  itemsSortBy,
} from '../../../batchDetails/types';
import {Divider, Skeleton} from 'native-base';
import {HomeScreens, ShopperScreens} from '@buncha/home/navigation/types';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {RefreshControl, RefreshControlProps} from 'react-native';
import {getClientType, getStoreName} from '@buncha/common/types/common';
import {useColorThemeType, useFontTheme} from '../../../appStyles/hooks/theme';
import {
  useGetItemStatusCount,
  useGetMinimumDeliveryStatus,
  useOrderAssignment,
  useSetDeliveryLocationStatusOfAssignedOrders,
} from '../../../batchDetails/hooks/OrderOperation';
import {
  useGetOrdersForOrderAssignee,
  useSkipPacking,
  useUpdateOrderItemStatus,
} from '../../../batchDetails/hooks/BatchDetails';

import AlertBox from '../AlertBox/AlertBox';
import {Analytics} from '@buncha/config/analytics/analytics';
import {B2BScreens} from '@buncha/B2B/navigation/types';
import {BottomButton} from '@buncha/components/composites/Button';
import {CustomFlatList} from '../../../components/core/list';
import {CustomText} from '@buncha/components/core/text';
import {EventType} from '@buncha/config/analytics/type';
import {H6Text} from '@buncha/components/core/text';
import If from '@buncha/components/conditional/If';
import {Modules} from '@buncha/utils/types';
import {PButton} from '@buncha/components/core/Button';
import PackItemAccordion from '../PackItemAccordion';
import RecheckBottomSheet from '../RecheckBottomSheet';
import RetryUpload from '@buncha/components/core/icon/RetryUpload';
import {RunType} from '@buncha/AboutShift/types';
import {SpinnerComponent} from '@buncha/components/core/loader';
import {Touchable} from '@buncha/components/core/touchable';
import {b2bNav} from '@buncha/B2B/navigation';
import font from '@buncha/appStyles/style/font';
import listener from '../../../config/listener';
import {navigation} from '@buncha/config/navigation';
import {scaleFonts} from '@buncha/appStyles/style/font';
import {smartLook} from '@buncha/config/analytics/smartlook';
import {styles} from './styles';
import {useAppSelector} from '@buncha/config/redux/hooks';
import {useBatchDetailContext} from '../../../batchDetails/hooks/batchDetail';
import {useGetLocale} from '@buncha/localization/hooks';
import {useIsMeijerStore} from '@buncha/home/hooks/home';
import {useRoute} from '@react-navigation/native';

interface PackItemListProps {
  runDetail?: RunForBatchDetail;
  navigateToReceiptScreen: () => void;
  assignmentModal: (value: boolean) => void;
  navigateToAddItem: (item: GroupedAssignedItems) => void;
}

function PackItemList(props: PackItemListProps) {
  const {runDetail, assignmentModal} = props;
  const muliFont = useFontTheme('muli');

  const theme = useColorThemeType();
  const {state, dispatch} = useBatchDetailContext();

  const {getOrderAssignee} = useOrderAssignment(dispatch);
  const [getOrdersForOrderAssignee, orderLoading] =
    useGetOrdersForOrderAssignee(dispatch);
  const [loading, setLoading] = useState<boolean>(false);
  const {updateOrderItemStatus} = useUpdateOrderItemStatus();
  const [setDeliveryLocationStatusOfAssignedOrders, packState] =
    useSetDeliveryLocationStatusOfAssignedOrders(dispatch);
  const {handleSkipPacking, skipPackingLoading} = useSkipPacking();
  const [getItemStatusCount, countState] = useGetItemStatusCount(dispatch);
  const {title, body, button} =
    useGetLocale(Modules.batchDetails, ShopperScreens.BatchDetailsPage) ?? {};
  const route = useRoute();
  const [activeItemIndex, setActiveItemIndex] = useState<number>(0);
  const [orderDeliveredStatus, setOrderDeliveredStatus] =
    useState<boolean>(false);
  const [needRefresh, setNeedRefresh] = useState(false);

  const [getMinimumDeliveryStatus, minStatusState] =
    useGetMinimumDeliveryStatus(dispatch);

  const [toDriver, setToDriver] = useState(false);
  const [isSkipSheet, setIsSkipSheet] = useState(false);
  const [isCheckSheet, setIsCheckSheet] = useState(false);
  const [firstOrderId, setFirstOrderId] = useState<number>(0);
  const [recheckSheet, setRecheckSheet] = useState(false);

  const groupedAssignedItems = state.groupedAssignedItems.all;

  const isMeijer = useIsMeijerStore(runDetail?.store?.id);

  const routeParams: any = route?.params;
  const [runId, setRunId] = useState(routeParams?.runId);

  const hasAllOrdersPacked = useMemo(() => {
    if (!groupedAssignedItems.length) return false;
    const allPackedOrderCount = groupedAssignedItems.filter(
      order =>
        order?.metaData?.order?.deliveryLocation?.deliveryStatus ===
          DeliveryLocationStatus.orderPacked ||
        (order?.metaData?.firstShopperName &&
          !order?.orderItems?.filter(
            item => item.itemStatus === OrderItemStatus.notPacked,
          )?.length),
    ).length;

    return allPackedOrderCount === groupedAssignedItems.length;
  }, [groupedAssignedItems]);

  const hasAllbagCountsGiven = useMemo(() => {
    if (!groupedAssignedItems.length) return false;
    const allBagCount = groupedAssignedItems.filter(
      order =>
        order?.metaData?.order?.bagsUsed > 0 ||
        Boolean(order?.metaData?.firstShopperName),
    ).length;
    return allBagCount === groupedAssignedItems.length;
  }, [groupedAssignedItems]);

  const toggleSheet = useCallback(
    (isSkip: boolean, isCheck: boolean) => {
      Analytics.eventWithProps('Recheck changes', {
        type: EventType.cta,
        runId: runId,
        countOfOrders: groupedAssignedItems?.length,
        storeType: getStoreName(String(runDetail?.store?.id)),
        client: getClientType(isMeijer),
      });
      setRecheckSheet(!recheckSheet);
      setIsSkipSheet(isSkip);
      setIsCheckSheet(isCheck);
    },
    [
      groupedAssignedItems?.length,
      isMeijer,
      recheckSheet,
      runDetail?.store?.id,
      runId,
    ],
  );

  const closeSheet = useCallback(() => {
    setRecheckSheet(!recheckSheet);
  }, [recheckSheet]);

  const onSkip = useCallback(() => {
    toggleSheet(true, false);
  }, [toggleSheet]);

  useEffect(() => {
    getOrderAssignee(runId as number);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (runDetail) {
      getMinimumDeliveryStatus(runDetail.id);
      setRunId(runDetail.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runDetail]);

  const packingFinished =
    minStatusState?.data === DeliveryLocationStatus.toBeAssigned;
  const orderPacked =
    minStatusState?.data === DeliveryLocationStatus.orderPacked;

  const findFirstNonPackedIndex = useCallback(() => {
    if (!groupedAssignedItems.length) return -2;
    for (let i = 0; i < groupedAssignedItems.length; i++) {
      const order = groupedAssignedItems[i];
      const deliveryStatus =
        order?.metaData?.order?.deliveryLocation?.deliveryStatus;
      const isPackingDone =
        deliveryStatus && deliveryStatus === DeliveryLocationStatus.orderPacked;

      if (!isPackingDone) return i;
    }
    return -1;
  }, [groupedAssignedItems]);

  const onRefresh = useCallback(() => {
    if (runId) {
      getMinimumDeliveryStatus(runId);
      getOrdersForOrderAssignee(
        runId,
        itemsForSection.all,
        itemsSortBy.customer,
      );
      getItemStatusCount(runId, [OrderItemStatus.notPacked]);
      let lastIndex = findFirstNonPackedIndex();
      if (lastIndex !== -2) setActiveItemIndex(lastIndex);
      setNeedRefresh(false);
    }
  }, [
    findFirstNonPackedIndex,
    getItemStatusCount,
    getMinimumDeliveryStatus,
    getOrdersForOrderAssignee,
    runId,
  ]);

  const toggleToDriver = useCallback(() => {
    assignmentModal(true);
    setToDriver(!toDriver);
  }, [assignmentModal, toDriver]);

  const updateItems = useCallback(() => {
    if (!countState.loading) {
      getItemStatusCount(runId, [
        OrderItemStatus.notPicked,
        OrderItemStatus.notPacked,
      ]);
      getOrdersForOrderAssignee(
        runId,
        itemsForSection.all,
        itemsSortBy.customer,
      );
      getMinimumDeliveryStatus(runId);
      setNeedRefresh(false);
    }
  }, [
    countState.loading,
    getItemStatusCount,
    getMinimumDeliveryStatus,
    getOrdersForOrderAssignee,
    runId,
  ]);

  const getItemStatus = useCallback((itemStatus: OrderItemStatus) => {
    switch (itemStatus) {
      case OrderItemStatus.dropped:
        return OrderItemStatus.notPacked;
      case OrderItemStatus.packed:
        return OrderItemStatus.notPacked;

      default:
        return OrderItemStatus.packed;
    }
  }, []);

  const changedItemStatus = useCallback(
    async (
      orderItem: OrderItems,
      status: OrderItemStatus,
      _lastItem: boolean,
    ) => {
      setNeedRefresh(true);
      const itemStatus = status ?? getItemStatus(orderItem?.itemStatus);
      if (runId) {
        Analytics.eventWithProps('Item Status changed', {
          type: EventType.cta,
          runID: runId,
          itemStatus: itemStatus,
          orderItemId: orderItem.id,
          client: getClientType(isMeijer),
        });
        await updateOrderItemStatus(
          runId,
          itemStatus,
          [orderItem.id],
          undefined,
          undefined,
          orderItem?.isAdjacentOOSItem,
        );
        if (
          orderItem.itemStatus === OrderItemStatus.dropped ||
          orderItem?.isAdjacentOOSItem
        )
          updateItems();
        else setNeedRefresh(true);
      }
    },
    [getItemStatus, runId, isMeijer, updateOrderItemStatus, updateItems],
  );

  const toggleItem = useCallback(
    (index: number) => {
      setActiveItemIndex(prevIndex => (prevIndex === index ? -1 : index));
    },
    [setActiveItemIndex],
  );

  const ordersPackedCount = useCallback(() => {
    const ordersPacked = state.groupedAssignedItems.all.filter(
      order =>
        order?.metaData?.order?.deliveryLocation?.deliveryStatus ===
        DeliveryLocationStatus.orderPacked,
    ).length;
    return ordersPacked;
  }, [state.groupedAssignedItems.all]);

  const itemsPackedCount = useCallback((orderItems: OrderItems[]) => {
    const itemsPacked = orderItems.filter(
      orderItem => orderItem?.itemStatus === OrderItemStatus.packed,
    ).length;
    return itemsPacked;
  }, []);

  const itemsUnPackedCount = useCallback((orderItems: OrderItems[]) => {
    const itemsNotPacked = orderItems.filter(
      orderItem =>
        orderItem?.itemStatus === OrderItemStatus.notPacked &&
        !orderItem?.isAdjacentOOSItem,
    ).length;
    return itemsNotPacked;
  }, []);

  useEffect(() => {
    const runType = runDetail?.runType;
    if (
      runId &&
      packingFinished &&
      runDetail?.receiptImageUrls &&
      runType === RunType.normal
    ) {
      Analytics.eventWithProps('Pass to Driver modal', {
        type: EventType.cta,
        runID: runId,
        packingFinished: packingFinished,
        client: getClientType(isMeijer),
      });
      assignmentModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packingFinished, runDetail?.receiptImageUrls]);

  const handleNavigation = useCallback(() => {
    const runType = runDetail?.runType;
    if (runType !== RunType.normal && runDetail?.masterRun?.id)
      navigation.navigate(HomeScreens.Shopper, {
        screen: ShopperScreens.VanLoadSite,
        params: {runId: runDetail?.id},
      });
    else toggleToDriver();
  }, [
    runDetail?.id,
    runDetail?.masterRun?.id,
    runDetail?.runType,
    toggleToDriver,
  ]);

  const B2BSTores: number[] = JSON.parse(
    useAppSelector(gState => gState.onboarding.constants?.B2B_STORE_IDS) ??
      '[]',
  );

  const handleFinishPacking = useCallback(async () => {
    if (runId && orderPacked && runDetail?.receiptImageUrls) {
      if (B2BSTores?.includes(runDetail?.store?.id))
        return navigation.replace(HomeScreens.B2B, {
          screen: B2BScreens.B2BCheckout,
          params: {
            runId: runId,
            storeId: runDetail?.store?.id,
            storeIndex: runDetail?.store?.elasticSearchIndex,
          },
        });
      await setDeliveryLocationStatusOfAssignedOrders(
        runId,
        DeliveryLocationStatus.toBeAssigned,
        state?.orderIdsToBeSend,
        () => {
          handleNavigation();
        },
      );
      return onRefresh();
    } else if (runId && packingFinished && runDetail?.receiptImageUrls) {
      assignmentModal(true);
    }
  }, [
    B2BSTores,
    assignmentModal,
    handleNavigation,
    onRefresh,
    orderPacked,
    packingFinished,
    runDetail?.receiptImageUrls,
    runDetail?.store?.elasticSearchIndex,
    runDetail?.store?.id,
    runId,
    setDeliveryLocationStatusOfAssignedOrders,
    state?.orderIdsToBeSend,
  ]);

  const handleMarkItemsAsPacked = useCallback(
    async (orderId: number, byPassCheck?: boolean) => {
      if (runId) {
        const ordersPacked = ordersPackedCount();
        if (!ordersPacked && !byPassCheck) {
          setFirstOrderId(orderId);
          toggleSheet(false, true);
          return;
        }
        Analytics.eventWithProps('Mark Items as Packed', {
          type: EventType.cta,
          runID: runId,
          client: getClientType(isMeijer),
        });
        setLoading(true);
        await setDeliveryLocationStatusOfAssignedOrders(
          runId,
          DeliveryLocationStatus.orderPacked,
          [orderId],
        );
        setOrderDeliveredStatus(true);
        setLoading(false);
      }
      listener.emit('orderPackRefresh');
      smartLook.trackCustomEvent('Mark Items as Packed', props);
      onRefresh();
    },
    [
      isMeijer,
      onRefresh,
      ordersPackedCount,
      props,
      runId,
      setDeliveryLocationStatusOfAssignedOrders,
      toggleSheet,
    ],
  );

  const renderItem = useCallback(
    (propes: {item: GroupedAssignedItems; index: number}) => {
      const {item, index} = propes;
      const deliveryStatus =
        item?.metaData?.order?.deliveryLocation?.deliveryStatus;
      const isPackingFinished =
        deliveryStatus &&
        deliveryStatus === DeliveryLocationStatus.toBeAssigned;

      return (
        <PackItemAccordion
          changedItemStatus={changedItemStatus}
          packingFinished={isPackingFinished}
          item={item}
          orderLoading={orderLoading || loading}
          runDetail={runDetail}
          isActive={index === activeItemIndex}
          toggleItem={() => toggleItem(index)}
          onRefresh={() => onRefresh()}
          itemsPackedCount={itemsPackedCount}
          itemsUnPackedCount={itemsUnPackedCount}
          handleMarkItemsAsPacked={handleMarkItemsAsPacked}
          orderDeliveredStatus={orderDeliveredStatus}
          needRefresh={needRefresh}
          navigateToAddItem={props.navigateToAddItem}
        />
      );
    },
    [
      activeItemIndex,
      changedItemStatus,
      handleMarkItemsAsPacked,
      itemsPackedCount,
      itemsUnPackedCount,
      loading,
      needRefresh,
      onRefresh,
      orderDeliveredStatus,
      orderLoading,
      props.navigateToAddItem,
      runDetail,
      toggleItem,
    ],
  );

  const Separator = useCallback(() => <Divider style={styles.divider} />, []);

  const onSkipPacking = useCallback(async () => {
    if (!runDetail?.id) return;
    setRecheckSheet(false);
    const res = await handleSkipPacking(
      runDetail?.id,
      DeliveryLocationStatus.orderPacked,
      state?.orderIdsToBeSend,
    );
    if (res && B2BSTores?.includes(runDetail?.store?.id))
      return b2bNav.B2BCheckout({
        runId,
        storeId: runDetail?.store?.id,
        storeIndex: runDetail?.store?.elasticSearchIndex,
      });
    onRefresh();
    listener.emit('orderPackRefresh');
    if (res) assignmentModal(true);
  }, [
    B2BSTores,
    assignmentModal,
    handleSkipPacking,
    onRefresh,
    runDetail?.id,
    runDetail?.store?.elasticSearchIndex,
    runDetail?.store?.id,
    runId,
    state?.orderIdsToBeSend,
  ]);

  const onClickRecheck = useCallback(async () => {
    Analytics.eventWithProps('Proceed changes', {
      type: EventType.cta,
      runId: runId,
      countOfOrders: groupedAssignedItems?.length,
      storeType: getStoreName(String(runDetail?.store?.id)),
      client: getClientType(isMeijer),
    });
    if (isSkipSheet) {
      onSkipPacking();
      Analytics.eventWithProps('Packing Skipped', {
        type: EventType.cta,
        runId: runId,
        countOfOrders: groupedAssignedItems?.length,
        storeType: getStoreName(String(runDetail?.store?.id)),
        client: getClientType(isMeijer),
      });
    } else if (isCheckSheet) {
      await handleMarkItemsAsPacked(firstOrderId, true);
      closeSheet();
    }
  }, [
    closeSheet,
    firstOrderId,
    groupedAssignedItems?.length,
    handleMarkItemsAsPacked,
    isCheckSheet,
    isMeijer,
    isSkipSheet,
    onSkipPacking,
    runDetail?.store?.id,
    runId,
  ]);

  useEffect(() => {
    if (runId)
      getOrdersForOrderAssignee(
        runId,
        itemsForSection.all,
        itemsSortBy.customer,
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]);

  useEffect(() => {
    if (runId) getItemStatusCount(runId, [OrderItemStatus.notPacked]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]);

  useEffect(() => {
    const itemStatusCount = listener.addListener(
      'orderPackDropRefresh',
      onRefresh,
    );
    return itemStatusCount;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box style={styles.parentBox}>
      {Separator()}
      <If
        condition={
          Boolean(groupedAssignedItems?.length) &&
          !hasAllOrdersPacked &&
          isMeijer
        }>
        <HBox
          style={[
            styles.skipContainer,
            {
              backgroundColor: theme?.background?.blue?.[200],
            },
          ]}>
          <H6Text
            style={{
              color: theme?.text?.blue?.[300],
            }}>
            {body?.alreadyPacked}
          </H6Text>
          <PButton
            title={button?.skip}
            style={[
              styles.skipButton,
              {
                backgroundColor: theme?.background?.blue?.[100],
              },
            ]}
            titleStyle={{
              fontFamily: muliFont[700].normal,
              fontSize: font.FontSize.fontH6.fontSize,
            }}
            isLoading={skipPackingLoading}
            changeStyleOnLoading
            loadingIndicator={<SpinnerComponent size={6} />}
            onPress={onSkip}
          />
        </HBox>
      </If>
      <If condition={runDetail}>
        <Box style={styles.alertBox}>
          <AlertBox title={title?.reminder} />
        </Box>
      </If>
      <CustomFlatList
        refreshControl={
          (
            <RefreshControl
              refreshing={countState.loading}
              onRefresh={onRefresh}
              tintColor={theme?.background.gray[100]}
              style={styles.refreshControl}
            />
          ) as React.ReactElement<RefreshControlProps>
        }
        data={state.groupedAssignedItems.all}
        renderItem={renderItem}
        ItemSeparatorComponent={Separator}
        ListFooterComponent={Separator}
        loading={orderLoading}
        showsVerticalScrollIndicator={false}
        loadingSkeleton={
          <Box style={styles.skeletonContainer}>
            <Skeleton marginTop={5} height={75} />
          </Box>
        }
      />
      <If condition={needRefresh}>
        <Touchable onPress={onRefresh}>
          <HBox
            style={[
              {backgroundColor: theme?.background.blue[100]},
              styles.container,
            ]}>
            <HBox>
              <CustomText
                style={[
                  {
                    color: theme?.text?.white[100],
                    fontFamily: muliFont[700].normal,
                  },
                  styles.updateTypo,
                ]}>
                Item status updated. Tap here to refresh
              </CustomText>
            </HBox>
            <RetryUpload
              width={scaleFonts(24)}
              height={scaleFonts(24)}
              viewBox="0 -1 16 16"
              fill={theme?.text?.white[100]}
            />
          </HBox>
        </Touchable>
      </If>
      <If
        condition={
          hasAllOrdersPacked &&
          (isMeijer || hasAllbagCountsGiven) &&
          state.groupedAssignedItems.all.length
        }>
        <BottomButton
          title={button?.finishPacking}
          disabled={packState.loading || needRefresh}
          isLoading={packState.loading}
          onPress={handleFinishPacking}
          loadingIndicator={<SpinnerComponent />}
          bgColor={theme?.background.blue[100]}
        />
      </If>
      <RecheckBottomSheet
        isOpen={recheckSheet}
        onClose={closeSheet}
        onSubmit={onClickRecheck}
        runId={runDetail?.id}
      />
    </Box>
  );
}

export default React.memo(PackItemList);
